import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, Building2, CalendarCheck, Zap, Bell,
  Settings, LogOut, ChevronLeft, ChevronRight, Menu
} from "lucide-react";

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const NAV_ITEMS = [
    { path: "/", label: t("nav.dashboard"), icon: LayoutDashboard, testId: "nav-dashboard" },
    { path: "/leads", label: t("nav.leads"), icon: Users, testId: "nav-leads" },
    { path: "/properties", label: t("nav.properties"), icon: Building2, testId: "nav-properties" },
    { path: "/visits", label: t("nav.visits"), icon: CalendarCheck, testId: "nav-visits" },
    { path: "/matching", label: t("nav.matching"), icon: Zap, testId: "nav-matching" },
    { path: "/settings", label: t("nav.settings"), icon: Settings, testId: "nav-settings" },
  ];

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        setUnreadCount(data.count);
      } catch {}
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data);
    } catch {}
  };

  const handleNotifClick = async () => {
    setShowNotifs(!showNotifs);
    if (!showNotifs) await loadNotifications();
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const currentLabel = NAV_ITEMS.find(i => i.path === location.pathname || (i.path !== "/" && location.pathname.startsWith(i.path)))?.label || "LeadVisits";

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8F9FA]">
      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 bg-white border-r border-border flex flex-col transition-all duration-200 ${collapsed ? "w-16" : "w-60"} ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-border">
          {!collapsed && <span className="font-black text-lg tracking-tight" style={{ fontFamily: 'Chivo, sans-serif', color: '#002FA7' }}>LeadVisits</span>}
          <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex items-center justify-center w-7 h-7 hover:bg-[#F1F3F5] rounded-sm transition-colors" data-testid="toggle-sidebar">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className="flex-1 py-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(item.path));
            return (
              <Link key={item.path} to={item.path} className={`sidebar-link ${active ? "active" : "text-[#495057]"}`} data-testid={item.testId} onClick={() => setMobileOpen(false)}>
                <item.icon size={18} strokeWidth={1.5} />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-border p-3 space-y-2">
          {!collapsed && <LanguageSwitcher compact />}
          {!collapsed && <div className="text-xs text-[#868E96] px-1 truncate">{user?.email}</div>}
          <button onClick={handleLogout} className="sidebar-link w-full text-[#C92A2A] hover:bg-red-50" data-testid="logout-button">
            <LogOut size={18} strokeWidth={1.5} />
            {!collapsed && <span>{t("auth.logout")}</span>}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-14 bg-white border-b border-border flex items-center justify-between px-4 lg:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-1.5 hover:bg-[#F1F3F5] rounded-sm" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu size={20} />
            </button>
            <div>
              <h1 className="text-sm font-bold text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{currentLabel}</h1>
              <span className="text-xs text-[#868E96] tracking-wider uppercase" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{user?.role}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 relative">
            <button onClick={handleNotifClick} className="relative p-2 hover:bg-[#F1F3F5] rounded-sm transition-colors" data-testid="notifications-button">
              <Bell size={18} strokeWidth={1.5} />
              {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#C92A2A] text-white text-[10px] font-bold flex items-center justify-center rounded-full">{unreadCount > 9 ? "9+" : unreadCount}</span>}
            </button>

            {showNotifs && (
              <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-border shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between p-3 border-b border-border">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#868E96]">{t("common.notifications")}</span>
                  {unreadCount > 0 && <button onClick={markAllRead} className="text-xs text-[#002FA7] hover:underline" data-testid="mark-all-read">{t("common.mark_all_read")}</button>}
                </div>
                {notifications.length === 0 ? (
                  <p className="p-4 text-sm text-[#868E96] text-center">{t("common.no_notifications")}</p>
                ) : (
                  notifications.map((n, i) => (
                    <div key={n.id || i} className={`p-3 border-b border-border last:border-0 ${!n.read ? "bg-blue-50/50" : ""}`}>
                      <p className="text-sm font-medium text-[#0A0A0A]">{n.title}</p>
                      <p className="text-xs text-[#495057] mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-[#868E96] mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
